import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { chatApi } from '../api/chat';
import type { ConversationView, SuggestedGroupView } from '../api/chat.types';
import { communityApi } from '../api/community';
import type { FeedPage, PostDetailView, PostType, PostView, ReportReason } from '../api/community.types';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { Button } from '../ds';
import { useAsync } from '../hooks/useAsync';
import { ComposePostPanel, type ComposableType } from '../screens/community/ComposePostPanel';
import { PostCard } from '../screens/community/PostCard';
import { PostDetailPanel } from '../screens/community/PostDetailPanel';
import { Avatar } from '../screens/social/Avatar';
import { Chip } from '../screens/social/Chip';
import { POST_TYPE_LABEL } from '../screens/social/postParts';
import { chatPath, useSocial } from '../screens/social/SocialContext';
import { SocialIcon, type SocialIconName } from '../screens/social/SocialIcon';
import { WebModal } from '../shell/WebModal';

/// "Tất cả" (undefined) rồi đúng thứ tự bản thiết kế; OTHER = các bài có chủ đề tự đặt.
const FILTERS: (PostType | undefined)[] = [undefined, 'QA', 'FIND_BUDDY', 'SHARE', 'ACHIEVEMENT', 'APP_SUPPORT', 'OTHER'];

const QUICK_COMPOSE: { icon: SocialIconName; label: string; type: ComposableType }[] = [
  { icon: 'comment', label: 'Đặt câu hỏi', type: 'QA' },
  { icon: 'card', label: 'Chia sẻ tài liệu', type: 'SHARE' },
  { icon: 'users', label: 'Tìm bạn học', type: 'FIND_BUDDY' },
];

const card: CSSProperties = { background: 'var(--surface-page)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-card)' };
const sectionLabel: CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-secondary)' };

function GroupRow({ name, mark, tone, sub, unread = 0, onClick, right }: { name: string; mark: string | null; tone?: number; sub: string; unread?: number; onClick?: () => void; right?: ReactNode }) {
  const content = (
    <>
      <Avatar name={name} mark={mark ?? name.charAt(0).toUpperCase()} size={40} tone={tone ?? 0} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>{sub}</span>
      </span>
      {right ??
        (unread > 0 && (
          <span
            style={{
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 999,
              background: 'var(--navy-500)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        ))}
    </>
  );
  const style: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '8px 0', minHeight: 56 };
  if (!onClick) return <div style={style}>{content}</div>;
  // Nền hover tràn ra 8px mỗi bên so với chữ — margin âm giữ chữ thẳng hàng với tiêu đề thẻ.
  return (
    <button
      type="button"
      className="sk-press"
      onClick={onClick}
      style={{ ...style, width: 'calc(100% + 16px)', margin: '0 -8px', padding: 8, border: 'none', borderRadius: 'var(--radius-control)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-vi)' }}
    >
      {content}
    </button>
  );
}

function SuggestedGroupRow({ group, onJoined }: { group: SuggestedGroupView; onJoined: (c: ConversationView) => void }) {
  const { toast } = useSocial();
  const [state, setState] = useState<'idle' | 'joining' | 'joined'>('idle');
  return (
    <GroupRow
      name={group.name}
      mark={group.mark}
      tone={2}
      sub={group.focus ? `${group.memberCount} thành viên · ${group.focus}` : `${group.memberCount} thành viên`}
      right={
        <Button
          size="sm"
          variant={state === 'joined' ? 'secondary' : 'primary'}
          disabled={state !== 'idle'}
          onClick={async () => {
            setState('joining');
            try {
              onJoined(await chatApi.joinGroup(group.id));
              setState('joined');
              toast('Đã tham gia nhóm');
            } catch {
              setState('idle');
              toast('Không tham gia được nhóm, thử lại sau');
            }
          }}
        >
          {state === 'joined' ? 'Đã vào' : 'Tham gia'}
        </Button>
      }
    />
  );
}

/** Cột phải: nhóm học của mình + nhóm gợi ý. Ẩn khi màn hẹp (< 900px). */
function CommunityAside() {
  const navigate = useNavigate();
  const [reloadKey, setReloadKey] = useState(0);
  const conversations = useAsync(() => chatApi.listConversations(), [reloadKey]);
  const suggested = useAsync(() => chatApi.getSuggestedGroups(), []);
  const groups = (conversations.data ?? []).filter((c) => c.kind === 'GROUP');

  return (
    <aside style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 0 }}>
      <div style={{ ...card, padding: '16px 16px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ ...sectionLabel, flex: 1 }}>Nhóm học của bạn</span>
          <button
            type="button"
            onClick={() => navigate('/chat')}
            style={{ border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'var(--font-vi)', fontSize: 13, fontWeight: 600, color: 'var(--link)', padding: '12px 4px', margin: '-12px 0' }}
          >
            Mở Chat
          </button>
        </div>
        {conversations.loading && !conversations.data ? (
          <div style={{ padding: '12px 0', fontSize: 13, color: 'var(--text-secondary)' }}>Đang tải…</div>
        ) : groups.length ? (
          groups.map((g) => (
            <GroupRow
              key={g.id}
              name={g.name}
              mark={g.mark}
              unread={g.unread}
              sub={`${g.memberCount} thành viên${g.onlineCount ? ` · ${g.onlineCount} online` : ''}`}
              onClick={() => navigate(chatPath(g.id))}
            />
          ))
        ) : (
          <div style={{ padding: '8px 0 12px', fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>Bạn chưa ở nhóm học nào. Tham gia một nhóm gợi ý hoặc tạo nhóm trong Chat.</div>
        )}
      </div>
      {!!suggested.data?.length && (
        <div style={{ ...card, padding: '16px 16px 8px' }}>
          <div style={{ ...sectionLabel, marginBottom: 4 }}>Nhóm gợi ý · cùng tiến độ</div>
          {suggested.data.map((g) => (
            <SuggestedGroupRow key={g.id} group={g} onJoined={() => setReloadKey((k) => k + 1)} />
          ))}
        </div>
      )}
    </aside>
  );
}

/**
 * SCR-COM-01 bản web: bảng tin + cột nhóm học; đăng bài và chi tiết bài mở trong modal.
 * Điện thoại (< 640px) theo bản mobile: không có hàng nút đăng nhanh, chip lọc cuộn ngang.
 */
export function WebCommunityPage({ narrow, phone }: { narrow: boolean; phone: boolean }) {
  const { me, toast } = useSocial();
  const [filter, setFilter] = useState<PostType | undefined>(undefined);
  const [reloadKey, setReloadKey] = useState(0);
  const [compose, setCompose] = useState<ComposableType | null>(null);
  const [open, setOpen] = useState<{ postId: string; authorName: string } | null>(null);

  const firstPage = useAsync(() => communityApi.getFeed(filter), [filter, reloadKey]);

  // Trang đầu vừa về thay toàn bộ danh sách; giữ list cũ trong lúc tải lại để không nháy spinner.
  const [page, setPage] = useState<FeedPage | null>(null);
  const [posts, setPosts] = useState<PostView[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  if (firstPage.data && firstPage.data !== page) {
    setPage(firstPage.data);
    setPosts(firstPage.data.items);
    setCursor(firstPage.data.nextCursor);
  }

  const [loadingMore, setLoadingMore] = useState(false);
  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await communityApi.getFeed(filter, cursor);
      setPosts((current) => [...(current ?? []), ...next.items]);
      setCursor(next.nextCursor);
    } catch {
      toast('Không tải thêm được bài');
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, filter, loadingMore, toast]);

  // Cuộn tới gần cuối danh sách thì tải trang sau.
  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !cursor) return;
    const observer = new IntersectionObserver((entries) => entries[0]?.isIntersecting && void loadMore(), { rootMargin: '400px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cursor, loadMore]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(null);
      setCompose(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const patchPost = (postId: string, patch: Partial<PostView>) =>
    setPosts((current) => current?.map((p) => (p.id === postId ? { ...p, ...patch } : p)) ?? null);

  const like = async (post: PostView) => {
    const liked = !post.liked;
    patchPost(post.id, { liked, likeCount: post.likeCount + (liked ? 1 : -1) });
    try {
      patchPost(post.id, await (liked ? communityApi.likePost(post.id) : communityApi.unlikePost(post.id)));
    } catch {
      patchPost(post.id, { liked: post.liked, likeCount: post.likeCount });
      toast('Chưa thích được, thử lại sau');
    }
  };

  const remove = async (post: PostView) => {
    if (!window.confirm('Xoá bài này? Bình luận và lượt thích cũng mất theo.')) return;
    try {
      await communityApi.deletePost(post.id);
      setPosts((current) => current?.filter((p) => p.id !== post.id) ?? null);
      toast('Đã xoá bài');
    } catch {
      toast('Chưa xoá được bài, thử lại sau');
    }
  };

  const report = async (post: PostView, reason: ReportReason) => {
    try {
      await communityApi.reportPost(post.id, reason);
      toast('Đã gửi báo cáo, cảm ơn bạn');
    } catch {
      toast('Chưa gửi được báo cáo, thử lại sau');
    }
  };

  const onDetailChange = useCallback((p: PostDetailView) => {
    const { liked, likeCount, commentCount, solved } = p;
    setPosts((current) => current?.map((x) => (x.id === p.id ? { ...x, liked, likeCount, commentCount, solved } : x)) ?? null);
  }, []);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: narrow ? 'minmax(0,1fr)' : 'minmax(0,1fr) 320px', gap: 24, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 680, width: '100%', justifySelf: 'center' }}>
        <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <button
            type="button"
            onClick={() => setCompose('QA')}
            aria-label="Tạo bài viết"
            style={{ display: 'flex', alignItems: 'center', gap: 12, border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          >
            <Avatar name={me?.name ?? ''} size={42} />
            <span
              className="sk-press-page"
              style={{
                flex: 1,
                minHeight: 46,
                display: 'flex',
                alignItems: 'center',
                padding: '0 18px',
                borderRadius: 999,
                border: '1px solid var(--border-default)',
                background: 'var(--surface-tint)',
                fontFamily: 'var(--font-vi)',
                fontSize: 14.5,
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Bạn muốn hỏi hay chia sẻ gì?
            </span>
          </button>
          {!phone && (
            <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border-default)', paddingTop: 12, flexWrap: 'wrap' }}>
              {QUICK_COMPOSE.map((q) => (
                <button
                  key={q.type}
                  type="button"
                  className="sk-press"
                  onClick={() => setCompose(q.type)}
                  style={{
                    flex: '1 1 140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    minHeight: 44,
                    borderRadius: 'var(--radius-control)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-vi)',
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: 'var(--ink)',
                  }}
                >
                  <SocialIcon name={q.icon} size={18} color="var(--cta-default)" />
                  {q.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div
          role="group"
          aria-label="Lọc theo loại bài"
          className={phone ? 'sk-scroll-x' : undefined}
          // Điện thoại: một hàng cuộn ngang tràn ra hai mép như app; màn rộng: xuống dòng.
          style={phone ? { display: 'flex', gap: 8, overflowX: 'auto', margin: '-8px -16px', padding: '0 16px' } : { display: 'flex', columnGap: 8, flexWrap: 'wrap', margin: '-8px 0' }}
        >
          {FILTERS.map((f) => (
            <Chip key={f ?? 'ALL'} label={f ? POST_TYPE_LABEL[f] : 'Tất cả'} active={filter === f} onClick={() => setFilter(f)} />
          ))}
        </div>

        {firstPage.loading && !posts ? (
          <div style={card}>
            <LoadingState />
          </div>
        ) : firstPage.error && !posts ? (
          <div style={card}>
            <ErrorState message="Không tải được bảng tin" onRetry={() => setReloadKey((k) => k + 1)} />
          </div>
        ) : posts?.length ? (
          posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onOpen={() => setOpen({ postId: p.id, authorName: p.author.name })}
              onLike={() => like(p)}
              onDelete={() => remove(p)}
              onReport={(reason) => report(p, reason)}
            />
          ))
        ) : (
          <div style={{ ...card, padding: 40, textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)' }}>Chưa có bài nào trong mục này.</div>
        )}
        <div ref={sentinel} />
        {loadingMore && <LoadingState />}
      </div>

      {!narrow && <CommunityAside />}

      {compose && (
        <WebModal width={560} onClose={() => setCompose(null)}>
          <ComposePostPanel
            initialType={compose}
            onClose={() => setCompose(null)}
            onPosted={() => {
              setCompose(null);
              setFilter(undefined);
              setReloadKey((k) => k + 1);
              toast('Đã đăng bài');
            }}
          />
        </WebModal>
      )}
      {open && (
        <WebModal width={640} onClose={() => setOpen(null)}>
          <PostDetailPanel postId={open.postId} authorName={open.authorName} onClose={() => setOpen(null)} onChange={onDetailChange} />
        </WebModal>
      )}
    </div>
  );
}
